/**
 * WHICH menu a guest orders from online, and which of its sections the website
 * hides — the storefront's own rule, read by every plane that decides a guest's
 * online menu: the storefront (`useAutoMenuSelection` + `MenuPageClient`), the
 * server's guest menu route (`GET /public/locations/:slug/menu`, which native
 * apps render), and the server's cart re-pricing at preview and checkout.
 *
 * 🔴 WHY IT LIVES HERE. The selection rule used to live in
 * `@order-up/client`, which the server cannot import. So the server priced a
 * cart against the RAW pin (`websiteConfig.parentMenuGroupId`) and fell back to
 * every root when the line was not under it, and the Orders app — which never
 * read the pin at all — sold items from menus the website does not offer.
 *
 * The website builder's contract for `websiteConfig.menuSections` (written by
 * `handleToggleMenuSection`, `apps/management/src/app/website/page.tsx`):
 *   - entries are keyed by `IMenuGroup.menuGroupId` and may name ANY group —
 *     a root menu or a sub-group at any depth;
 *   - a group is hidden from the website iff an entry with its id has
 *     `visible === false`; a missing entry, an empty array or no config means
 *     VISIBLE;
 *   - `name` and `position` are snapshots frozen at toggle time — renderers
 *     read names and order from the live groups, never from these.
 *
 * Pure: no Firestore, no React, no clock.
 */

import type { LocationWebsiteConfig } from '../locations/Location';
import type { IMenuGroup } from './MenuGroup';
import { isMenuGroupChannelEnabled } from './menuGroupAvailability';
import { hiddenWebsiteMenuGroupIds } from './scheduledMenuOnline';

/** The half of `websiteConfig` the section toggles live in. */
export type WebsiteMenuSectionsConfig =
  | Pick<LocationWebsiteConfig, 'menuSections'>
  | null
  | undefined;

/** The half of `websiteConfig` that decides a guest's online menu: its pin and its hidden sections. */
export type WebsiteMenuSelectionConfig =
  | Pick<LocationWebsiteConfig, 'parentMenuGroupId' | 'menuSections'>
  | null
  | undefined;

/**
 * Whether a menu group is visible on the restaurant's website per the
 * `websiteConfig.menuSections` toggles.
 *
 * Only an explicit `{ menuGroupId, visible: false }` entry hides a group;
 * every other state (no config, no `menuSections`, no entry for this group,
 * `visible: true`) is visible. Accepts a group object or a raw
 * `menuGroupId`; a group without an id is treated as visible.
 *
 * This checks ONLY the website toggle — combine with
 * `isMenuGroupAvailableForOnlineOrdering` (or
 * {@link isMenuGroupOnlineChannelEnabled} for time-stable contexts) for full
 * online-menu filtering.
 */
export function isMenuGroupVisibleOnWebsite(
  config: WebsiteMenuSectionsConfig,
  group: Pick<IMenuGroup, 'menuGroupId'> | number | null | undefined,
): boolean {
  const menuGroupId = typeof group === 'number' ? group : group?.menuGroupId;
  if (menuGroupId == null) return true;
  const entry = config?.menuSections?.find((section) => section.menuGroupId === menuGroupId);
  return entry ? entry.visible !== false : true;
}

/**
 * Static (time-independent) subset of `isMenuGroupAvailableForOnlineOrdering`:
 * active, not archived, `visibleInOnlineOrdering` not explicitly off, and
 * `availability.online` not explicitly off.
 *
 * Use this where selection must be STABLE across the day (root-menu
 * auto-selection, builder dropdowns) — the full evaluator's seasonal/date/
 * day/time-window clauses would flip the selection intraday. Rendered menu
 * trees still apply the full evaluator.
 */
export function isMenuGroupOnlineChannelEnabled(
  group: Pick<IMenuGroup, 'isActive' | 'isArchived' | 'visibleInOnlineOrdering' | 'availability'>,
): boolean {
  return isMenuGroupChannelEnabled(group, 'ONLINE_ORDERING');
}

/** The fields {@link selectParentMenuGroups} reads. */
export type ParentMenuGroupFields = Pick<IMenuGroup, 'isArchived' | 'isActive'> & {
  readonly parentIds?: readonly number[] | null;
};

/**
 * The LIVE root menu groups: no parent, not archived, not explicitly inactive
 * (`isActive !== false` keeps legacy docs without the field visible). What
 * `useParentMenuGroups` serves and menu auto-selection runs over.
 */
export function selectParentMenuGroups<G extends ParentMenuGroupFields>(groups: readonly G[]): G[] {
  return groups.filter(
    (group) =>
      (!group.parentIds || group.parentIds.length === 0) &&
      !group.isArchived &&
      group.isActive !== false,
  );
}

/** Why the storefront opened on the menu it did. */
export type OrderingMenuSelectionReason = 'website-config' | 'single-menu' | 'none';

export interface OrderingMenuSelection<G> {
  /** The menu the storefront opens on, or null when the guest must choose. */
  readonly selected: G | null;
  /**
   * Root menus currently offerable on the website: live, online-channel
   * enabled, and not hidden via `websiteConfig.menuSections`. The list a menu
   * picker presents when `selected` is null.
   */
  readonly candidates: G[];
  readonly reason: OrderingMenuSelectionReason;
}

/** The fields {@link selectOrderingMenu} reads off a root. */
export type OrderingMenuRootFields = Pick<
  IMenuGroup,
  'menuGroupId' | 'isActive' | 'isArchived' | 'visibleInOnlineOrdering' | 'availability'
>;

/**
 * Which menu the storefront opens on, over ONLINE CANDIDATES — live root menu
 * groups (`selectParentMenuGroups`) that pass the static online-channel check
 * ({@link isMenuGroupOnlineChannelEnabled}; time windows are deliberately
 * excluded so selection is stable intraday) and the website visibility toggles
 * ({@link isMenuGroupVisibleOnWebsite}):
 *
 * 1. `websiteConfig.parentMenuGroupId`, when it names a candidate. A stale pin
 *    (archived / deleted / hidden / POS-only group) falls through instead of
 *    serving a broken menu.
 * 2. The single candidate, when there is exactly one — so archived legacy menus
 *    or POS-only roots never block the single-menu case.
 * 3. Otherwise none: the guest picks from `candidates`.
 *
 * @param rootGroups the location's root menu groups (`selectParentMenuGroups`)
 */
export function selectOrderingMenu<G extends OrderingMenuRootFields>(
  rootGroups: readonly G[],
  websiteConfig: WebsiteMenuSelectionConfig,
): OrderingMenuSelection<G> {
  const candidates = rootGroups.filter(
    (group) =>
      isMenuGroupOnlineChannelEnabled(group) && isMenuGroupVisibleOnWebsite(websiteConfig, group),
  );
  if (candidates.length === 0) return { selected: null, candidates, reason: 'none' };

  const pinned = websiteConfig?.parentMenuGroupId;
  if (pinned) {
    const specified = candidates.find((group) => group.menuGroupId === pinned);
    if (specified) return { selected: specified, candidates, reason: 'website-config' };
  }

  if (candidates.length === 1) return { selected: candidates[0]!, candidates, reason: 'single-menu' };

  return { selected: null, candidates, reason: 'none' };
}

/**
 * Where a guest can be ordering from online: the menu the storefront selects —
 * or, when it selects none, every menu its picker offers — and the sections the
 * website hides. What the server holds a cart line to, so a line from a menu the
 * website does not offer, or from a hidden section, is refused rather than
 * priced on whatever path happens to reach it.
 */
export interface OnlineStorefrontScope {
  /** The selected root, else every picker candidate. Empty: nothing is orderable online. */
  readonly rootIds: readonly number[];
  /** `hiddenWebsiteMenuGroupIds(websiteConfig)`. */
  readonly hiddenSectionIds: ReadonlySet<number>;
}

export function resolveOnlineStorefrontScope(
  menuGroups: readonly IMenuGroup[],
  websiteConfig: WebsiteMenuSelectionConfig,
): OnlineStorefrontScope {
  const selection = selectOrderingMenu(selectParentMenuGroups(menuGroups), websiteConfig);
  const roots = selection.selected ? [selection.selected] : selection.candidates;
  return {
    rootIds: roots.flatMap((root) => (root.menuGroupId != null ? [root.menuGroupId] : [])),
    hiddenSectionIds: hiddenWebsiteMenuGroupIds(websiteConfig),
  };
}
